/**
 * FRE-139: tunings the player created on this device. Stored in localStorage as a JSON array of the same strings that
 * go on tabs (see `formatCustomTuning`), de-duplicated and capped. Every access is guarded: private mode just means
 * the list is not remembered.
 */
import { useSyncExternalStore } from 'react';
import { customTuningId, findTuning, parseCustomTuning, type Tuning } from './tunings.js';

export const CUSTOM_TUNINGS_KEY = 'fb-custom-tunings';
export const CUSTOM_TUNINGS_MAX = 50;

const listeners = new Set<() => void>();
let cacheRaw: string | null | undefined;
let cacheValue: string[] = [];

function read(): string[] {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(CUSTOM_TUNINGS_KEY);
  } catch {
    return cacheValue;
  }
  if (raw === cacheRaw) return cacheValue;
  let list: string[] = [];
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) list = parsed.filter((s): s is string => typeof s === 'string' && Boolean(parseCustomTuning(s)));
  } catch {
    list = [];
  }
  cacheRaw = raw;
  cacheValue = list;
  return list;
}

function write(list: string[]) {
  try {
    localStorage.setItem(CUSTOM_TUNINGS_KEY, JSON.stringify(list));
  } catch {
    /* not remembered in private mode */
  }
  cacheRaw = undefined;
  cacheValue = list;
  listeners.forEach((l) => l());
}

/** Saved custom tuning strings (stable array identity until the list changes). */
export function listCustomTunings(): string[] {
  return read();
}

/** Saves a stored custom string. Returns false when it is invalid or already saved (no duplicates by notes). */
export function addCustomTuning(stored: string): boolean {
  const parsed = parseCustomTuning(stored);
  if (!parsed) return false;
  const id = customTuningId(parsed.notes);
  const list = read();
  if (list.some((s) => customTuningId(parseCustomTuning(s)?.notes ?? []) === id)) return false;
  write([...list, stored.trim()].slice(-CUSTOM_TUNINGS_MAX));
  return true;
}

/** Removes the saved tuning with this tuning id (`custom:<notes>`). Tabs that already store the string are unaffected. */
export function removeCustomTuning(id: string) {
  const list = read();
  const next = list.filter((s) => customTuningId(parseCustomTuning(s)?.notes ?? []) !== id);
  if (next.length !== list.length) write(next);
}

export function subscribeCustomTunings(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === CUSTOM_TUNINGS_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** Saved custom tunings as Tuning objects (category 'custom'), re-rendering when the list changes. */
export function useCustomTunings(): Tuning[] {
  const list = useSyncExternalStore(subscribeCustomTunings, listCustomTunings, listCustomTunings);
  return resolve(list);
}

let resolvedFor: string[] | null = null;
let resolved: Tuning[] = [];
function resolve(list: string[]): Tuning[] {
  if (resolvedFor !== list) {
    resolvedFor = list;
    resolved = list.map((s) => findTuning(s)).filter((t): t is Tuning => Boolean(t) && t!.category === 'custom');
  }
  return resolved;
}

/** Non-hook lookup of a saved custom tuning by id (tuner store). */
export function savedCustomById(id: string): Tuning | undefined {
  return resolve(read()).find((t) => t.id === id);
}
